import React, { Fragment, FC, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import Filter from 'dibs-icons/exports/Filter';
import tracking from 'dibs-tracking';

import { SbRespRefineTopNav_itemSearch } from './__generated__/SbRespRefineTopNav_itemSearch.graphql';
import { SbRespRefineTopNav_user } from './__generated__/SbRespRefineTopNav_user.graphql';
import { SbRespRefineTopNav_viewer } from './__generated__/SbRespRefineTopNav_viewer.graphql';
import { SbRespRefineTopFilters } from './SbRespRefineTopFilters';
import { SbRespRefineMenuDrawer } from '../SbRespRefineMenuDrawer/SbRespRefineMenuDrawer';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { trackSearchWithinResults } from '../../utils/tracking/searchBrowse/filterTracking';
import { SbSharedRefineMenuSearchWithin } from '../SbSharedRefineMenu/SbSharedRefineMenuSearchWithin/SbSharedRefineMenuSearchWithin';
import { SbRespRefineTopAppliedFilters } from './SbRespRefineTopAppliedFilters';

import styles from './SbRespRefineTopNav.scss';

type Props = {
    itemSearch: SbRespRefineTopNav_itemSearch;
    viewer: SbRespRefineTopNav_viewer;
    user: SbRespRefineTopNav_user;
    currency: string;
    isClient: boolean;
    isTrade: boolean;
};

const SbRespRefineTopNavComponent: FC<Props> = ({
    itemSearch,
    user,
    viewer,
    isClient,
    isTrade,
    currency,
}) => {
    const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
    return (
        <Fragment>
            <div className={styles.topSection}>
                <SbRespRefineTopFilters
                    itemSearch={itemSearch}
                    viewer={viewer}
                    user={user}
                    currency={currency}
                    isClient={isClient}
                />
                <div
                    className={styles.allFiltersButton}
                    onClick={() => {
                        setIsFiltersDrawerOpen(true);
                        tracking.trackEvent({
                            category: 'results page refinement',
                            action: 'all filters opened',
                        });
                    }}
                >
                    <Filter className={styles.filterIcon} />
                    <FormattedMessage
                        id="SbRespRefineTopNav.allFiltersCta"
                        defaultMessage="All Filters"
                    />
                </div>
            </div>
            <div className={styles.middleSection}>
                <SbSharedRefineMenuSearchWithin
                    horizontalSpacing="small"
                    onSubmit={trackSearchWithinResults}
                    itemSearch={itemSearch}
                />
                <div className={styles.sortAndPriceContainer}>
                    <span className={styles.label}>
                        <FormattedMessage
                            id="SbRespRefineTopNav.pageSizeLabel"
                            defaultMessage="View"
                        />
                    </span>
                    <SbRespPageSize itemSearch={itemSearch} usePageSizeLinks />
                    <span className={styles.label}>
                        <FormattedMessage
                            id="SbRespRefineTopNav.sortOrderLabel"
                            defaultMessage="Sort By"
                        />
                    </span>
                    <SbRespSortItems itemSearch={itemSearch} />
                </div>
            </div>
            <div className={styles.bottomSection}>
                <SbRespRefineTopAppliedFilters
                    currency={currency}
                    itemSearch={itemSearch}
                    user={user}
                />
            </div>
            <SbRespRefineMenuDrawer
                itemSearch={itemSearch}
                viewer={viewer}
                user={user}
                currency={currency}
                isClient={isClient}
                isTrade={isTrade}
                isOpen={isFiltersDrawerOpen}
                closeDrawer={() => setIsFiltersDrawerOpen(false)}
            />
        </Fragment>
    );
};

export const SbRespRefineTopNav = createFragmentContainer(SbRespRefineTopNavComponent, {
    itemSearch: graphql`
        fragment SbRespRefineTopNav_itemSearch on ItemSearchQueryConnection {
            ...SbRespRefineTopAppliedFilters_itemSearch
            ...SbRespSortItems_itemSearch
            ...SbRespPageSize_itemSearch
            ...SbRespRefineTopFilters_itemSearch
            ...SbSharedRefineMenuSearchWithin_itemSearch
            ...SbRespRefineMenuDrawer_itemSearch
        }
    `,
    user: graphql`
        fragment SbRespRefineTopNav_user on User {
            ...SbRespRefineTopAppliedFilters_user
            ...SbRespRefineTopFilters_user
            ...SbRespRefineMenuDrawer_user
        }
    `,
    viewer: graphql`
        fragment SbRespRefineTopNav_viewer on Viewer {
            ...SbRespRefineTopFilters_viewer
            ...SbRespRefineMenuDrawer_viewer
        }
    `,
});
